import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { HelpUtils } from './help.utils';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { ApiRootFacade } from '@schir-int-client/api-root';
import { take } from 'rxjs/operators';
import { firstValueFrom, Subscription } from 'rxjs';

@Component({
	selector: 'schir-int-client-help',
	templateUrl: './help.component.html',
	styleUrls: ['./help.component.scss'],
	standalone: false,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HelpComponent implements AfterViewInit, OnDestroy {
	private readonly TOC_URL = 'toc.html';
	private readonly CHROME_HELP_URL = 'https://support.google.com/chrome/?p=help';
	private readonly EDGE_HELP_URL = 'https://support.microsoft.com/microsoft-edge';

	constructor(
		private helpUtils: HelpUtils,
		private domSanitizer: DomSanitizer,
		private cdr: ChangeDetectorRef,
		private dialogRef: MatDialogRef<HelpComponent>,
		private http: HttpClient,
		apiRootFacade: ApiRootFacade
	) {
		this.helpUtils.init();
		apiRootFacade
			.getCurrentApiRoot()
			.pipe(take(1))
			.subscribe(ar => {
				this.baseUrl = ar._links.self.href.replace('api', 'assets/help/');
				this.helpFileUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(this.baseUrl + this.TOC_URL);
			});
	}

	private subscriptions: Subscription[] = [];

	private baseUrl: string;

	helpAnchorsForVisibleElements: string[] = [''];

	helpFileUrl: SafeResourceUrl;

	linkByAnchor: Map<string, { url: string; text: string; indent: number }> = new Map();
	browserHelpLink: string | null = null;
	browserHelpLabel: string | null = null;

	async ngAfterViewInit(): Promise<void> {
		this.initBrowserHelpLink();
		try {
			const data = await firstValueFrom(this.http.get<any[]>('/assets/help/anchors.json'));
			this.linkByAnchor = new Map(data);
			this.linkByAnchor.set('', { url: this.TOC_URL, text: 'Inhaltsverzeichnis', indent: 0 });

			this.subscriptions.push(
				this.helpUtils.shallRefreshAnchors.subscribe(anchors => {
					this.helpAnchorsForVisibleElements = anchors.visible;
					this.selectAnchor(anchors.focused || '');
					this.cdr.detectChanges();
				})
			);
		} catch (err) {
			console.error('Error loading anchors.json:', err);
		}
	}

	private initBrowserHelpLink(): void {
		const ua = navigator.userAgent;
		if (ua.includes('Edg/')) {
			this.browserHelpLink = this.EDGE_HELP_URL;
			this.browserHelpLabel = 'Hilfe zum Browser (Edge)';
			return;
		}
		if (ua.includes('Chrome/') && !ua.includes('OPR/') && !ua.includes('Edg/')) {
			this.browserHelpLink = this.CHROME_HELP_URL;
			this.browserHelpLabel = 'Hilfe zum Browser (Chrome)';
		}
	}

	openBrowserHelp(event: Event): void {
		event.preventDefault();
		if (this.browserHelpLink) {
			window.open(this.browserHelpLink, '_blank', 'noopener');
		}
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach(s => s.unsubscribe());
	}

	selectAnchor(anchor: string) {
		const link = this.linkByAnchor.get(anchor);
		if (link) {
			this.helpFileUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(this.baseUrl + link.url);
		} else {
			console.error('Error: invalid help anchor: ' + anchor);
		}
	}

	closeDialog() {
		this.dialogRef.close();
	}

	newTab() {
		const url = this.helpFileUrl as any;
		const urlString = url?.changingThisBreaksApplicationSecurity;
		if (urlString) {
			// machen wir, da vom Nutzer explizit gefordert
			window.open(urlString, 'schir_help');
			this.closeDialog();
		}
	}

	getTitle(anchor: string) {
		return this.linkByAnchor?.get(anchor)?.text;
	}

	getIndentEm(anchor: string): number | null {
		return this.linkByAnchor?.get(anchor)?.indent;
	}
}
